// React
import { useMemo } from "react";

// Hooks
import { useCitContextData } from "@DataPages/context/cit/CitContext";

// MUI
import { Fade, Grid, Stack, Typography } from "@mui/material";

// Layouts
import { LoadingSpinner } from "@/Layouts/components/LoadingSpinner";

// Components
import MultiGroupedBarChartAnalyticsSkeleton from "@DataPages/layouts/financeManagement/skeletons/MultiGroupedBarChartAnalyticsSkeleton.tsx";
import GradientBarGraph from "@/DataPages/components/FniManagement/components/CIT/GradientBarGraph/GradientBarGraph";
import SeverityBarIndicator from "@DataPages/layouts/financeManagement/widgets/SeverityBarIndicator";

// Helpers
import { generateAnalyticsData } from "@DataPages/helpers/financeManagement/cit/analytics";

// Types
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";

interface ICitModelAnalyticsProps {
	selected: IPeriodTreeSelection;
	isLoading: boolean;
	isFetching: boolean;
}

export const CitModelAnalytics = (props: Readonly<ICitModelAnalyticsProps>) => {
	const { selected, isLoading, isFetching } = props;

	const { filteredReceivables, receivables, isUnfunded, citSettings } = useCitContextData();

	const maxYValues = citSettings?.citMaxGraphValues ?? {
		maxUnfundedDollars: 5,
		maxUnfundedContracts: 200,
	};

	// Fn to send the data to the charts when a search is made
	// Includes only the CIT that are contracts in transit and balances greater than sysSettingsCit.citMinimumBalance
	const unfundedReceivables = useMemo(() => {
		return receivables?.filter(
			(receivable) => (!filteredReceivables || filteredReceivables?.includes(receivable._id)) && isUnfunded(receivable)
		);
	}, [receivables, citSettings, filteredReceivables]);

	const analyticsData = useMemo(() => {
		const citGraphWidth = parseInt((citSettings?.citGraphWidth ?? "").replace(" Days", ""), 10) || 0;
		return generateAnalyticsData({ deals: unfundedReceivables, graphWidth: citGraphWidth, propToUse: "balance" });
	}, [unfundedReceivables, citSettings]);

	if (isLoading) {
		return <MultiGroupedBarChartAnalyticsSkeleton />;
	}

	if (!receivables) {
		return null;
	}

	return (
		<Stack justifyContent={"space-between"} alignItems={"center"} spacing={4} pt={4} pb={6}>
			<Grid container>
				<Grid item xs={12}>
					{selected?.titleExtended && (
						<Typography className="text-default !font-montserrat !text-[24px]/[1.2] !font-medium">
							{selected.titleExtended} Contracts In Transit Analytics
						</Typography>
					)}
				</Grid>
				<Fade in={!isLoading && isFetching} timeout={400}>
					<div className="mt-2">
						<LoadingSpinner label="Contracts In Transit is still loading..." className="items-center" />
					</div>
				</Fade>
				<Grid item xs={12} className="!mt-2">
					<Grid container direction="row">
						<Grid item xs={6} className="pl-10">
							<GradientBarGraph
								title="Unfunded Dollars"
								data={analyticsData}
								xAxis="age"
								dataKey="amount"
								maxY={maxYValues?.maxUnfundedDollars ?? 5}
								symbol="$"
								postfix="M"
								isLoading={isLoading}
								yAxisLabel="Amounts ($)"
								xAxisLabel="Transit (days)"
							/>
						</Grid>
						<Grid item xs={6}>
							<GradientBarGraph
								title="Unfunded Contracts"
								data={analyticsData}
								xAxis="age"
								dataKey="quantity"
								maxY={maxYValues?.maxUnfundedContracts ?? 200}
								isLoading={isLoading}
								yAxisLabel="# of Contracts"
								xAxisLabel="Transit (days)"
							/>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<SeverityBarIndicator />
		</Stack>
	);
};
